"use client";

import {
	Activity,
	AlertTriangle,
	CheckCircle2,
	Code,
	ExternalLink,
	MessageSquare,
	Server,
	ShieldCheck,
	Trophy,
	Zap,
} from "lucide-react";

import { Badge } from "@/lib/components/badge";
import {
	MIN_SIGNIFICANT_REQUESTS,
	useModelBenchmarks,
} from "@/lib/use-model-benchmarks";
import { cn } from "@/lib/utils";

import { CarrierMark, getProviderIcon } from "@llmgateway/shared/components";
import { formatNumber } from "@llmgateway/shared/number-format";

export function ModelBenchmarks({
	modelId,
	uploadedIcons = {},
}: {
	modelId: string;
	// Carrier-uploaded marks (Airside claims), keyed by provider id.
	uploadedIcons?: Record<string, string>;
}) {
	const { data, isLoading } = useModelBenchmarks(modelId);

	const providers = data?.providers ?? [];
	const arena = data?.arena;
	const hasProviderData = providers.some((p) => p.logsCount > 0);
	const hasArenaData = arena?.text !== null || arena?.code !== null;

	if (isLoading) {
		return (
			<div className="rounded-lg border border-border p-6">
				<div className="h-6 w-48 animate-pulse rounded bg-muted mb-4" />
				<div className="space-y-3">
					{[1, 2, 3].map((i) => (
						<div key={i} className="h-16 animate-pulse rounded bg-muted" />
					))}
				</div>
			</div>
		);
	}

	if (!hasProviderData && !hasArenaData) {
		return null;
	}

	const sorted = [...providers]
		.filter((p) => p.logsCount > 0)
		.sort((a, b) => {
			if (a.uptime === null && b.uptime === null) {
				return b.logsCount - a.logsCount;
			}
			if (a.uptime === null) {
				return 1;
			}
			if (b.uptime === null) {
				return -1;
			}
			if (b.uptime !== a.uptime) {
				return b.uptime - a.uptime;
			}
			return b.logsCount - a.logsCount;
		});

	// Pick the most stable provider: highest uptime among those with meaningful
	// traffic. Require a statistically significant request count to avoid
	// awarding a provider that only handled a handful of calls.
	const stabilityCandidates = sorted.filter(
		(p) => p.uptime !== null && p.logsCount >= MIN_SIGNIFICANT_REQUESTS,
	);
	const mostStableProviderId =
		stabilityCandidates.length > 1 ? stabilityCandidates[0].providerId : null;

	return (
		<div className="space-y-8">
			{/* Arena Benchmarks */}
			{hasArenaData && (
				<div>
					<div className="flex items-center gap-2 mb-4">
						<Trophy className="h-5 w-5 text-muted-foreground" />
						<h2 className="text-xl md:text-2xl font-semibold">
							Quality Benchmarks
						</h2>
					</div>
					<p className="text-sm text-muted-foreground mb-4">
						Crowdsourced quality ratings from{" "}
						<a
							href={arena?.source ?? "https://arena.ai/leaderboard"}
							target="_blank"
							rel="noopener noreferrer"
							className="underline hover:text-foreground inline-flex items-center gap-1"
						>
							Chatbot Arena
							<ExternalLink className="h-3 w-3" />
						</a>
						. Higher ELO score = better quality. Updated{" "}
						{arena?.fetchedAt ?? "recently"}.
					</p>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						{arena?.text && (
							<div className="rounded-lg border border-border p-4">
								<div className="flex items-center gap-2 mb-3">
									<MessageSquare className="h-4 w-4 text-blue-500" />
									<span className="text-sm font-medium">Overall (Text)</span>
								</div>
								<div className="flex items-baseline gap-3">
									<span className="text-3xl font-bold tabular-nums">
										{arena.text.score}
									</span>
									<span className="text-sm text-muted-foreground">ELO</span>
								</div>
								<div className="mt-2 flex items-center gap-2">
									<Badge
										variant="outline"
										className={cn(
											"text-xs",
											arena.text.rank <= 10
												? "text-amber-600 border-amber-500/50"
												: arena.text.rank <= 30
													? "text-blue-600 border-blue-500/50"
													: "",
										)}
									>
										#{arena.text.rank}
									</Badge>
									<span className="text-xs text-muted-foreground truncate">
										{arena.text.matchedName}
									</span>
								</div>
							</div>
						)}
						{arena?.code && (
							<div className="rounded-lg border border-border p-4">
								<div className="flex items-center gap-2 mb-3">
									<Code className="h-4 w-4 text-purple-500" />
									<span className="text-sm font-medium">Coding</span>
								</div>
								<div className="flex items-baseline gap-3">
									<span className="text-3xl font-bold tabular-nums">
										{arena.code.score}
									</span>
									<span className="text-sm text-muted-foreground">ELO</span>
								</div>
								<div className="mt-2 flex items-center gap-2">
									<Badge
										variant="outline"
										className={cn(
											"text-xs",
											arena.code.rank <= 10
												? "text-amber-600 border-amber-500/50"
												: arena.code.rank <= 30
													? "text-blue-600 border-blue-500/50"
													: "",
										)}
									>
										#{arena.code.rank}
									</Badge>
									<span className="text-xs text-muted-foreground truncate">
										{arena.code.matchedName}
									</span>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Provider Performance Benchmarks */}
			{sorted.length > 0 && (
				<div>
					<div className="flex items-center gap-2 mb-4">
						<Activity className="h-5 w-5 text-muted-foreground" />
						<h2 className="text-xl md:text-2xl font-semibold">
							Provider Performance
						</h2>
					</div>
					<p className="text-sm text-muted-foreground mb-4">
						Real performance data from LLM Gateway over the last{" "}
						{sorted[0]?.windowHours ?? 24} hours. Higher uptime and throughput
						are better. Stats are hidden for providers with under{" "}
						{formatNumber(MIN_SIGNIFICANT_REQUESTS)} requests, where the sample
						is too small to be reliable.
					</p>

					<div className="grid gap-3">
						{sorted.map((provider) => {
							const uploadedIcon = uploadedIcons[provider.providerId];
							const ProviderIcon = getProviderIcon(provider.providerId);
							const isMostStable = provider.providerId === mostStableProviderId;
							const isSignificant =
								provider.logsCount >= MIN_SIGNIFICANT_REQUESTS;
							const insufficientDataTitle = `Not enough traffic for reliable stats (under ${formatNumber(MIN_SIGNIFICANT_REQUESTS)} requests)`;

							return (
								<div
									key={provider.providerId}
									className={cn(
										"rounded-lg border p-4 transition-colors",
										isMostStable
											? "border-green-500/50 bg-green-50/50 dark:bg-green-950/20"
											: "border-border",
									)}
								>
									<div className="flex items-center justify-between gap-4 flex-wrap">
										<div className="flex items-center gap-3 min-w-0">
											{uploadedIcon ? (
												<CarrierMark
													src={uploadedIcon}
													alt={provider.providerName}
													className="h-5 w-5 shrink-0 object-contain"
												/>
											) : ProviderIcon ? (
												<ProviderIcon className="h-5 w-5 shrink-0" />
											) : (
												<Server className="h-5 w-5 shrink-0 text-muted-foreground" />
											)}
											<div className="min-w-0">
												<div className="flex items-center gap-2">
													<span className="font-medium truncate">
														{provider.providerName}
													</span>
													{isMostStable && (
														<Badge
															variant="outline"
															className="text-green-600 border-green-500/50 text-xs gap-1"
														>
															<ShieldCheck className="h-3 w-3" />
															Most stable
														</Badge>
													)}
												</div>
												<span className="text-xs text-muted-foreground">
													{formatNumber(provider.logsCount)} requests
												</span>
											</div>
										</div>

										<div className="flex items-center gap-6 text-sm">
											<div className="text-center">
												<div className="flex items-center gap-1 text-muted-foreground mb-0.5">
													<Zap className="h-3 w-3" />
													<span className="text-xs">Throughput</span>
												</div>
												<span
													className={cn(
														"font-mono font-medium",
														!isSignificant || provider.tokensPerSecond === null
															? "text-muted-foreground"
															: "",
													)}
													title={
														isSignificant
															? "Output tokens per second across all requests in the window"
															: insufficientDataTitle
													}
												>
													{isSignificant && provider.tokensPerSecond !== null
														? `${formatNumber(provider.tokensPerSecond)} tok/s`
														: "\u2014"}
												</span>
											</div>

											<div className="text-center">
												<div className="flex items-center gap-1 text-muted-foreground mb-0.5">
													<CheckCircle2 className="h-3 w-3" />
													<span className="text-xs">Uptime</span>
												</div>
												<span
													className={cn(
														"font-mono font-medium",
														!isSignificant || provider.uptime === null
															? "text-muted-foreground"
															: provider.uptime >= 99
																? "text-green-600"
																: provider.uptime >= 95
																	? "text-amber-500"
																	: "text-red-500",
													)}
													title={
														isSignificant
															? `Uptime over last ${provider.windowHours}h`
															: insufficientDataTitle
													}
												>
													{isSignificant && provider.uptime !== null
														? `${provider.uptime.toFixed(2)}%`
														: "\u2014"}
												</span>
											</div>

											<div className="text-center hidden sm:block">
												<div className="flex items-center gap-1 text-muted-foreground mb-0.5">
													<AlertTriangle className="h-3 w-3" />
													<span className="text-xs">Errors</span>
												</div>
												<span
													className={cn(
														"font-mono font-medium",
														!isSignificant
															? "text-muted-foreground"
															: provider.errorRate > 5
																? "text-red-500"
																: provider.errorRate > 1
																	? "text-amber-500"
																	: "text-green-600",
													)}
													title={
														isSignificant ? undefined : insufficientDataTitle
													}
												>
													{isSignificant ? `${provider.errorRate}%` : "—"}
												</span>
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
